import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CertificationQuestionGenerationSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function isCertificationEnriched(item) {
  return item && Array.isArray(item.answer_options);
}

/**
 * Парсит ответ API со списком вопросов. Вопросы могут приходить строкой с разделителем ";"
 * (например: "Вопрос 1? ; Вопрос 2? ; Вопрос 3?" или "[ ... ; ... ; ... ]") или массивом.
 * Возвращает массив элементов: либо { text } для фазы 1, либо полный объект (question, answer_options, correct_answer_index), если API вернул готовые варианты.
 */
function parseQuestionsFromResponse(data) {
  if (data == null) return [];
  const result = [];
  if (typeof data === 'string') {
    const cleaned = data.replace(/^[\s\[\]\n"]+|[\s\[\]\n"]+$/g, '').trim();
    const parts = cleaned.split(/\s*;\s*/).map((s) => s.trim()).filter(Boolean);
    result.push(...parts.map((text) => ({ text })));
  } else if (Array.isArray(data)) {
    for (const item of data) {
      if (typeof item === 'string') {
        const trimmed = item.replace(/^[\s\[\]\n"]+|[\s\[\]\n"]+$/g, '').trim();
        if (trimmed.includes(';')) {
          const split = trimmed.split(/\s*;\s*/).map((s) => s.trim()).filter(Boolean);
          result.push(...split.map((text) => ({ text })));
        } else if (trimmed) {
          result.push({ text: trimmed });
        }
      } else if (item && typeof item === 'object') {
        const questionText = (item.question ?? item.text ?? '').trim();
        const opts = item.answer_options;
        const hasRealOptions = Array.isArray(opts) && opts.some((o) => (o || '').trim());
        if (hasRealOptions) {
          result.push({
            question: questionText,
            answer_options: opts,
            correct_answer_index: Math.max(0, Math.min(3, item.correct_answer_index ?? 0)),
          });
        } else if (questionText) {
          result.push({ text: questionText });
        }
      }
    }
  }
  return result;
}

function CertificationQuestionGenerationSubTab() {
  const [topicId, setTopicId] = useState('');
  const [text, setText] = useState('');
  const [generatedItems, setGeneratedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingAnswers, setCreatingAnswers] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/certification-topics`);
        setTopics(response.data);
      } catch (err) {
        console.error('Ошибка при загрузке тем сертификаций:', err);
      }
    };
    fetchTopics();
  }, []);

  const handleGenerate = async () => {
    if (!text.trim()) {
      setError('Введите текст для генерации вопросов');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/certification-questions/generate`, { text: text.trim() });
      const raw = response.data?.questions ?? response.data;
      const items = parseQuestionsFromResponse(raw);
      setGeneratedItems(items.length > 0 ? items : [{ text: '' }]);
    } catch (err) {
      setError('Ошибка при генерации вопросов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnswers = async () => {
    const toCreate = generatedItems.filter((item) => !isCertificationEnriched(item) && item.text && item.text.trim());
    if (toCreate.length === 0) {
      setError('Нет вопросов для создания ответов или добавьте текст вопроса');
      return;
    }
    setCreatingAnswers(true);
    setError(null);
    const updated = [...generatedItems];
    for (let i = 0; i < updated.length; i++) {
      if (!isCertificationEnriched(updated[i]) && updated[i].text && updated[i].text.trim()) {
        try {
          const res = await axios.post(`${API_URL}/api/certification-questions/generate-answer`, {
            question_text: updated[i].text.trim(),
          });
          const d = res.data;
          let opts = Array.isArray(d.answer_options) ? d.answer_options : [];
          if (opts.length < 4) opts = [...opts, ...Array(4 - opts.length).fill('')];
          if (opts.length > 4) opts = opts.slice(0, 4);
          updated[i] = {
            question: (d.question || '').trim(),
            answer_options: opts,
            correct_answer_index: Math.max(0, Math.min(3, d.correct_answer_index ?? 0)),
            is_correct: d.is_correct,
          };
        } catch (err) {
          setError(`Ошибка при создании ответов для вопроса ${i + 1}: ${err.response?.data?.detail || err.message}`);
          setCreatingAnswers(false);
          return;
        }
      }
    }
    setGeneratedItems(updated);
    setCreatingAnswers(false);
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...generatedItems];
    updated[index] = { ...updated[index], [field]: value };
    setGeneratedItems(updated);
  };

  const handleOptionChange = (qIndex, optIndex, value) => {
    const updated = [...generatedItems];
    const opts = [...(updated[qIndex].answer_options || [])];
    while (opts.length <= optIndex) opts.push('');
    opts[optIndex] = value;
    updated[qIndex] = { ...updated[qIndex], answer_options: opts };
    setGeneratedItems(updated);
  };

  const handleCorrectChange = (qIndex, correctIndex) => {
    const updated = [...generatedItems];
    updated[qIndex] = { ...updated[qIndex], correct_answer_index: correctIndex };
    setGeneratedItems(updated);
  };

  const handleDeleteQuestion = (index) => {
    setGeneratedItems(generatedItems.filter((_, i) => i !== index));
  };

  const handleAddQuestion = () => {
    if (generatedItems.length > 0 && generatedItems.every(isCertificationEnriched)) {
      setGeneratedItems([
        ...generatedItems,
        { question: '', answer_options: ['', '', '', ''], correct_answer_index: 0 },
      ]);
    } else {
      setGeneratedItems([...generatedItems, { text: '' }]);
    }
  };

  const addOption = (qIndex) => {
    const updated = [...generatedItems];
    const opts = [...(updated[qIndex].answer_options || []), ''];
    updated[qIndex] = { ...updated[qIndex], answer_options: opts };
    setGeneratedItems(updated);
  };

  const removeOption = (qIndex, optIndex) => {
    const updated = [...generatedItems];
    let opts = [...(updated[qIndex].answer_options || [])];
    if (opts.length <= 2) return;
    opts = opts.filter((_, i) => i !== optIndex);
    let correct = updated[qIndex].correct_answer_index;
    if (correct >= opts.length) correct = opts.length - 1;
    if (correct > optIndex) correct -= 1;
    updated[qIndex] = { ...updated[qIndex], answer_options: opts, correct_answer_index: Math.max(0, correct) };
    setGeneratedItems(updated);
  };

  const handleSave = async () => {
    if (!topicId || generatedItems.length === 0) {
      setError('Выберите сертификацию и добавьте хотя бы один вопрос');
      return;
    }
    const enriched = generatedItems.filter(isCertificationEnriched);
    if (enriched.length === 0) {
      setError('Нажмите «Создать ответы» и дождитесь завершения');
      return;
    }
    const invalid = enriched.filter(
      (q) => !q.question.trim() || !(q.answer_options || []).filter((o) => o.trim()).length >= 2
    );
    if (invalid.length > 0) {
      setError('У каждого вопроса должен быть текст и минимум 2 непустых варианта ответа');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        topic_id: parseInt(topicId, 10),
        questions: enriched.map((q) => {
          const rawOpts = q.answer_options || [];
          const opts = rawOpts.filter((o) => o.trim() !== '');
          const correctText = rawOpts[q.correct_answer_index];
          let idx = opts.findIndex((o) => o === correctText);
          if (idx < 0) idx = 0;
          return {
            text: q.question.trim(),
            answer_options: opts,
            correct_answer_index: idx,
          };
        }),
      };
      const response = await axios.post(`${API_URL}/api/certification-questions/save-generated`, payload);
      alert(`Сохранено вопросов: ${response.data.saved_count}`);
      setText('');
      setGeneratedItems([]);
    } catch (err) {
      setError('Ошибка при сохранении: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getOptions = (q) => {
    const opts = q.answer_options || [];
    if (opts.length < 4) {
      const padded = [...opts];
      while (padded.length < 4) padded.push('');
      return padded;
    }
    return opts;
  };

  const hasPhase1Items = generatedItems.length > 0 && generatedItems.some((item) => !isCertificationEnriched(item));
  const allEnriched = generatedItems.length > 0 && generatedItems.every(isCertificationEnriched);

  if (generatedItems.length === 0) {
    return (
      <div className="cert-question-generation generation-container">
        {error && <div className="error-message">{error}</div>}
        <div className="form-container">
          <h3>Генерация вопросов для сертификации</h3>
          <div className="form-group">
            <label htmlFor="cert-topic">Сертификация (тема) *</label>
            <select
              id="cert-topic"
              value={topicId}
              onChange={(e) => setTopicId(e.target.value)}
              disabled={loading || !topics.length}
            >
              <option value="">{topics.length ? 'Выберите сертификацию' : 'Нет тем'}</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
            {!topics.length && (
              <small className="form-hint">Сначала создайте тему во вкладке «Темы сертификаций»</small>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="cert-text">Текст для анализа *</label>
            <textarea
              id="cert-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={12}
              placeholder="Вставьте текст, по которому нужно сгенерировать список вопросов. Затем нажмите «Сгенерировать вопросы», отредактируйте список и нажмите «Создать ответы»."
              disabled={loading}
            />
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={loading || !text.trim() || !topicId}
            >
              {loading ? 'Генерация...' : 'Сгенерировать вопросы'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cert-question-generation generation-container">
      {error && <div className="error-message">{error}</div>}

      <div className="form-container" style={{ marginBottom: '1rem' }}>
        <div className="form-group">
          <label htmlFor="cert-topic">Сертификация (тема) *</label>
          <select
            id="cert-topic"
            value={topicId}
            onChange={(e) => setTopicId(e.target.value)}
            disabled={loading || creatingAnswers || !topics.length}
          >
            <option value="">{topics.length ? 'Выберите сертификацию' : 'Нет тем'}</option>
            {topics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="cert-text">Текст для анализа *</label>
          <textarea
            id="cert-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder="Вставьте текст для генерации списка вопросов..."
            disabled={loading || creatingAnswers}
          />
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={loading || creatingAnswers || !text.trim() || !topicId}
          >
            {loading ? 'Генерация...' : 'Сгенерировать вопросы'}
          </button>
        </div>
      </div>

      {hasPhase1Items && (
        <>
          <div className="generated-questions-header">
            <h3>Сгенерированные вопросы ({generatedItems.length})</h3>
            <div className="header-actions">
              <button type="button" className="btn btn-secondary" onClick={handleAddQuestion}>
                + Добавить вопрос
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateAnswers}
                disabled={creatingAnswers || !generatedItems.some((item) => item.text && item.text.trim())}
              >
                {creatingAnswers ? 'Создание ответов...' : 'Создать ответы'}
              </button>
            </div>
          </div>
          <div className="generated-questions-list">
            {generatedItems.map((item, index) => (
              <div key={index} className="generated-question-card" style={{ marginBottom: '0.5rem' }}>
                <div className="question-card-header">
                  <div className="question-card-header-left">
                    <span className="question-number">Вопрос {index + 1}</span>
                    {item.is_correct !== undefined && (
                      <span className={`question-validation-badge ${item.is_correct ? 'validation-correct' : 'validation-incorrect'}`}>
                        {item.is_correct ? 'Корректен' : 'Не корректен'}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-icon btn-delete"
                    onClick={() => handleDeleteQuestion(index)}
                    title="Удалить вопрос"
                  >
                    ✕
                  </button>
                </div>
                <div className="form-group full-width" style={{ margin: 0 }}>
                  <textarea
                    value={item.text ?? ''}
                    onChange={(e) => handleQuestionChange(index, 'text', e.target.value)}
                    rows={2}
                    placeholder="Текст вопроса"
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {allEnriched && (
        <>
          <div className="generated-questions-header">
            <h3>Сгенерированные вопросы ({generatedItems.length})</h3>
            <div className="header-actions">
              <button type="button" className="btn btn-secondary" onClick={handleAddQuestion}>
                + Добавить вопрос
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || !topicId}
                title={!topicId ? 'Выберите сертификацию выше' : ''}
              >
                {saving ? 'Сохранение...' : 'Сохранить в базу'}
              </button>
            </div>
          </div>
          <div className="cert-generation-topic-row">
            <label>Сертификация для сохранения:</label>
            <select value={topicId} onChange={(e) => setTopicId(e.target.value)} disabled={saving}>
              <option value="">Выберите сертификацию</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </div>
          <div className="generated-questions-list">
            {generatedItems.map((q, qIndex) => {
              const options = getOptions(q);
              return (
                <div key={qIndex} className="generated-question-card">
                  <div className="question-card-header">
                    <div className="question-card-header-left">
                      <span className="question-number">Вопрос {qIndex + 1}</span>
                      {q.is_correct !== undefined && (
                        <span className={`question-validation-badge ${q.is_correct ? 'validation-correct' : 'validation-incorrect'}`}>
                          {q.is_correct ? 'Корректен' : 'Не корректен'}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-icon btn-delete"
                      onClick={() => handleDeleteQuestion(qIndex)}
                      title="Удалить вопрос"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="question-card-body">
                    <div className="form-group full-width">
                      <label>Текст вопроса *</label>
                      <textarea
                        value={q.question}
                        onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}
                        rows={2}
                        placeholder="Текст вопроса"
                      />
                    </div>
                    <div className="form-group full-width">
                      <label>Варианты ответов (отметьте правильный) *</label>
                      {options.map((opt, optIndex) => (
                        <div key={optIndex} className="answer-option-row">
                          <input
                            type="text"
                            value={opt}
                            onChange={(e) => handleOptionChange(qIndex, optIndex, e.target.value)}
                            placeholder={`Вариант ${optIndex + 1}`}
                            className="answer-option-input"
                          />
                          <button
                            type="button"
                            className={`btn-checkbox ${q.correct_answer_index === optIndex ? 'checked' : ''}`}
                            onClick={() => handleCorrectChange(qIndex, optIndex)}
                            title={q.correct_answer_index === optIndex ? 'Правильный ответ' : 'Сделать правильным'}
                          >
                            {q.correct_answer_index === optIndex ? '✓' : '○'}
                          </button>
                          {options.length > 2 && (
                            <button
                              type="button"
                              className="btn-remove"
                              onClick={() => removeOption(qIndex, optIndex)}
                              title="Удалить вариант"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      <button type="button" className="btn-add-option" onClick={() => addOption(qIndex)}>
                        + Добавить вариант
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default CertificationQuestionGenerationSubTab;
